import React, { useState, useContext, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { User, Users } from "react-feather";
import Select from "react-select";
import Swal from "sweetalert2";
import reservasContext from "../../context/reservas/reservasContext";
import authContext from "../../context/authentication/authContex";
import clienteAxios from "../../config/axios";

function ModalEditarReserva({ show, onHide, reserva, onReservaActualizada }) {
  const rContext = useContext(reservasContext);
  //socios se carga una unica vez en reservasContext al iniciar sesion y se refresca por socket.
  const { actualizarReserva, loading, socios, loadingSocios } = rContext;

  const aContext = useContext(authContext);
  const { usuario, checkIfAdmin, checkIfGestorReservas, checkIfSocio } = aContext;

  // Estados del formulario
  const [jugadores, setJugadores] = useState([]);
  const [error, setError] = useState(null);
  const [sociosSeleccionados, setSociosSeleccionados] = useState([]);
  const [reservanteCambiado, setReservanteCambiado] = useState(false);

  // Inicializar jugadores con los datos de la reserva
  useEffect(() => {
    if (show && reserva && reserva.jugadores) {
      setJugadores([...reserva.jugadores]);

      // Pre-seleccionar los socios si ya están cargados
      if (socios.length > 0) {
        const sociosPreseleccionados = reserva.jugadores.map(jugador => {
          return socios.find(socio => socio.userName === jugador.userNameJ) || null;
        });
        setSociosSeleccionados(sociosPreseleccionados);
      }
    }
  }, [show, reserva, socios]);

  // Resetear estados al cerrar el modal
  useEffect(() => {
    if (!show) {
      setSociosSeleccionados([]);
      setJugadores([]);
      setError(null);
      setReservanteCambiado(false);

      // Limpiar padding-right que Bootstrap Modal agrega al body
      document.body.style.paddingRight = '';
      document.body.style.overflow = '';
    }
  }, [show]);

  // Handler para seleccionar socio en cualquier posición
  const handleSeleccionarSocio = (index, opcionSeleccionada) => {
    // Validar si el jugador original tiene cargos antes de reemplazarlo
    const jugadorOriginal = reserva?.jugadores?.[index];
    const nuevoUserName = opcionSeleccionada?.userName;

    if (jugadorOriginal && jugadorOriginal.userNameJ && jugadorOriginal.userNameJ !== nuevoUserName) {
      const tieneCargos = (jugadorOriginal.servicios && jugadorOriginal.servicios.length > 0) ||
                          (reserva.cargosAdicionales && reserva.cargosAdicionales.length > 0);
      if (tieneCargos) {
        setError("El jugador que intenta reemplazar tiene cargos adicionales aplicados, elimínelos primero");
        return;
      }
    }

    setError(null);
    const nuevosSociosSeleccionados = [...sociosSeleccionados];
    nuevosSociosSeleccionados[index] = opcionSeleccionada;
    setSociosSeleccionados(nuevosSociosSeleccionados);

    const nuevosJugadores = [...jugadores];
    if (opcionSeleccionada) {
      nuevosJugadores[index] = {
        nombre: opcionSeleccionada.fullName || opcionSeleccionada.userName,
        shortName: opcionSeleccionada.shortName || '',
        userNameJ: opcionSeleccionada.userName,
      };
    } else {
      nuevosJugadores[index] = { nombre: "", shortName: "", userNameJ: "" };
    }
    setJugadores(nuevosJugadores);
  };

  // Obtener opciones disponibles para un selector específico (excluyendo los ya seleccionados en otros)
  const getOpcionesDisponibles = (indexActual) => {
    return socios.filter((socio) => {
      // Verificar si este socio ya está seleccionado en otro índice
      const yaSeleccionado = sociosSeleccionados.some((socioSel, idx) => {
        return idx !== indexActual && socioSel && socioSel.value === socio.value;
      });
      return !yaSeleccionado;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!reserva || !reserva._id) {
      setError("No se encontró la reserva a editar");
      return;
    }

    try {
      // Validar que un socio no pueda editar una reserva cuyo horario ya pasó
      const esSocio = checkIfSocio();
      if (esSocio) {
        const ahora = new Date();
        const fechaReserva = new Date(reserva.fecha);
        const fechaSinHora = new Date(fechaReserva.getUTCFullYear(), fechaReserva.getUTCMonth(), fechaReserva.getUTCDate());
        const hoySinHora = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());

        if (fechaSinHora < hoySinHora) {
          setError("No podés editar una reserva cuyo horario ya pasó.");
          return;
        }

        if (fechaSinHora.getTime() === hoySinHora.getTime()) {
          const [hInicio, mInicio] = reserva.horaInicio.split(":").map(Number);
          const minutosInicio = hInicio * 60 + mInicio;
          const minutosActuales = ahora.getHours() * 60 + ahora.getMinutes();
          if (minutosInicio < minutosActuales) {
            setError("No podés editar una reserva cuyo horario ya pasó.");
            return;
          }
        }
      }

      // Validar que todos los jugadores estén seleccionados
      const cantidadEsperada = (reserva.tipoJuego === "SINGLES" || reserva.tipoJuego === "SINGLES_45" || reserva.tipoJuego === "SINGLES_PADEL") ? 2 : 4;
      for (let i = 0; i < cantidadEsperada; i++) {
        if (!jugadores[i] || !jugadores[i].nombre || jugadores[i].nombre.trim() === "") {
          setError(`Debes seleccionar todos los jugadores. Falta el Jugador ${i + 1}`);
          return;
        }
      }

      // Validar que el usuario que edita, si es socio, esté incluido como jugador
      const editorEsSocio = checkIfSocio();
      if (editorEsSocio) {
        const editorEstaEnJugadores = jugadores.some(
          (jugador) => jugador.userNameJ === usuario.userName
        );
        if (!editorEstaEnJugadores) {
          setError("Debes incluirte como jugador en la reserva.");
          return;
        }
      }

      // Validar que el reservante (quien hizo la reserva), si es socio, esté incluido como jugador
      const reservanteUserName = reservanteCambiado ? usuario.userName : reserva.userName;
      const reservanteFullName = reservanteCambiado ? (usuario.fullName || usuario.userName) : (reserva.userFullName || reserva.userName);
      const reservanteEsSocio = socios.some(
        (socio) => socio.userName === reservanteUserName
      );

      if (reservanteEsSocio) {
        const reservanteEstaEnJugadores = jugadores.some(
          (jugador) => jugador.userNameJ === reservanteUserName
        );

        if (!reservanteEstaEnJugadores) {
          setError(`El socio que reservó (${reservanteFullName}) debe estar incluido como jugador. Para poder quitarlo, cambia quien reserva.`);
          return;
        }
      }

      // Variable para datos de observada (se llena si REEMPLAZO_BLOQUEADO y el usuario confirma)
      let observadaData = null;

      // Validar que ningún jugador tenga reservas que excedan el límite o en el mismo día
      const jugadoresUserNames = jugadores
        .filter(j => j.userNameJ)
        .map(j => j.userNameJ);

      if (jugadoresUserNames.length > 0) {
        try {
          // Formatear fecha en formato YYYY-MM-DD (usando UTC para evitar desfase de timezone)
          const fechaReserva = new Date(reserva.fecha);
          const year = fechaReserva.getUTCFullYear();
          const month = String(fechaReserva.getUTCMonth() + 1).padStart(2, "0");
          const day = String(fechaReserva.getUTCDate()).padStart(2, "0");
          const fechaFormateada = `${year}-${month}-${day}`;

          const token = localStorage.getItem("token");
          const validacionResponse = await clienteAxios.post(
            "/cancha-reservas/validar-jugadores-disponibles",
            {
              jugadoresUserNames,
              fecha: fechaFormateada,
              excludeReservaId: reserva._id,
              tipoJuego: reserva.tipoJuego,
              horaInicio: reserva.horaInicio,
              horaFin: reserva.horaFin,
              presencial: reserva.presencial || false
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const { disponibles, jugadoresOcupados, maxReservasActivas, motivoBloqueo, fecAlt } = validacionResponse.data.data;

          if (!disponibles) {
            if (motivoBloqueo === "SOCIO_BLOQUEADO") {
              const nombres = jugadoresOcupados.map(jo => `${jo.nombre} - ${jo.reserva.motivo}`).join("\n");
              setError(`No se puede guardar. Socio(s) bloqueado(s) para reservar:\n\n${nombres}`);
              return;
            }

            if (motivoBloqueo === "SOCIO_AUTOBLOQUEADO") {
              const motivos = jugadoresOcupados.map(jo => jo.reserva.motivo).join("\n");
              setError(`No se puede guardar.\n\n${motivos}`);
              return;
            }

            const mensajesOcupados = jugadoresOcupados.map(jo => {
              const f = new Date(jo.reserva.fecha);
              const fechaLocal = new Date(f.getUTCFullYear(), f.getUTCMonth(), f.getUTCDate());
              const fechaFormateada = fechaLocal.toLocaleDateString("es-AR");
              return `${jo.nombre} tiene una reserva en ${jo.reserva.cancha} el ${fechaFormateada} de ${jo.reserva.horaInicio} a ${jo.reserva.horaFin}`;
            }).join("\n");

            // REEMPLAZO_BLOQUEADO: warning con opción de guardar como observada
            if (motivoBloqueo === "REEMPLAZO_BLOQUEADO") {
              const fecAltLocal = new Date(fecAlt);
              const fechaHoraCreacion = `${fecAltLocal.toLocaleDateString("es-AR")} ${String(fecAltLocal.getHours()).padStart(2,'0')}:${String(fecAltLocal.getMinutes()).padStart(2,'0')} hs`;
              const observacionTexto = `Los siguientes reemplazos tenían reservas vigentes (no finalizadas) en el momento (${fechaHoraCreacion}) en que se creó esta reserva:\n${mensajesOcupados}`;

              const resultado = await Swal.fire({
                title: "Reserva Observada",
                html: `<p>Los siguientes reemplazos tenían reservas vigentes (no finalizadas) en el momento (<b>${fechaHoraCreacion}</b>) en que se creó esta reserva:</p><p style="white-space:pre-line; text-align:left;">${mensajesOcupados}</p><p>Si continúa, la reserva quedará marcada como <b>observada</b>.</p>`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Guardar como observada",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#f59e0b",
              });

              if (!resultado.isConfirmed) return;

              // Marcar para guardar como observada
              observadaData = { observada: true, observacion: observacionTexto };
            } else {
              let mensajeError = "";
              if (motivoBloqueo === "SOLAPAMIENTO_CROSS_DEPORTE") {
                mensajeError = `No se puede guardar los cambios porque los siguientes jugadores ya tienen una reserva en otro deporte en el mismo horario:\n\n${mensajesOcupados}`;
              } else if (motivoBloqueo === "MISMO_DIA") {
                mensajeError = `No se puede guardar los cambios porque los siguientes jugadores ya tienen una reserva vigente (no finalizada) del mismo deporte en el mismo día:\n\n${mensajesOcupados}`;
              } else if (motivoBloqueo === "LIMITE_GLOBAL") {
                mensajeError = `No se puede guardar los cambios porque los siguientes jugadores superan el máximo permitido (${maxReservasActivas}) de reservas vigentes (no finalizadas) del mismo deporte:\n\n${mensajesOcupados}`;
              } else {
                mensajeError = `No se puede guardar los cambios:\n\n${mensajesOcupados}`;
              }

              setError(mensajeError);
              return;
            }
          }
        } catch (validacionError) {
          console.error("Error al validar jugadores disponibles:", validacionError);
          setError("Error al validar disponibilidad de jugadores");
          return;
        }
      }

      // Preparar datos de actualización
      const reservaData = {
        jugadores: jugadores,
      };

      // Si la reserva queda observada, incluir la marca y descripción
      if (observadaData) {
        reservaData.observada = observadaData.observada;
        reservaData.observacion = observadaData.observacion;
      }

      // Si el admin/gestor cambió el reservante, incluir los nuevos datos
      if (reservanteCambiado) {
        reservaData.userID = usuario._id;
        reservaData.userName = usuario.userName;
        reservaData.userFullName = usuario.fullName || usuario.userName;
      }

      await actualizarReserva(reserva._id, reservaData);

      // Si llegamos aquí, la reserva se actualizó exitosamente
      console.log("✅ Reserva actualizada exitosamente");
      onReservaActualizada();
    } catch (error) {
      console.error("❌ Error al actualizar reserva:", error);
      // El error ya se muestra desde el context con SweetAlert
    }
  };

  if (!reserva) {
    return null;
  }

  const cantidadJugadores = (reserva.tipoJuego === "SINGLES" || reserva.tipoJuego === "SINGLES_45" || reserva.tipoJuego === "SINGLES_PADEL") ? 2 : 4;
  // Usar duración almacenada en la reserva: respeta históricas (75min) y nuevas (90min en pádel).
  const duracionMinutos = reserva.duracionMinutos
    ?? (reserva.tipoJuego === "SINGLES_45" ? 45
      : reserva.tipoJuego === "DOBLES" ? 75
      : (reserva.tipoJuego === "SINGLES_PADEL" || reserva.tipoJuego === "DOBLES_PADEL") ? 90
      : 60);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      backdrop="static"
      centered
      style={{ zIndex: 9999 }}
      backdropClassName="modal-backdrop-high-z"
    >
      <Modal.Header closeButton>
        <Modal.Title>Editar Reserva</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger" style={{ whiteSpace: 'pre-line' }}>{error}</Alert>}

          {/* Información de la reserva (no editable) */}
          <div className="mb-4 p-3 bg-light rounded">
            <Row>
              <Col md={6}>
                <p className="mb-1"><strong>Cancha:</strong> {reserva.nameActivo}</p>
                <p className="mb-1"><strong>Fecha:</strong> {(() => {
                  const f = new Date(reserva.fecha);
                  const fechaLocal = new Date(f.getUTCFullYear(), f.getUTCMonth(), f.getUTCDate());
                  return fechaLocal.toLocaleDateString("es-AR");
                })()}</p>
              </Col>
              <Col md={6}>
                <p className="mb-1"><strong>Horario:</strong> {reserva.horaInicio} - {reserva.horaFin}</p>
                <p className="mb-1"><strong>Tipo:</strong> {reserva.tipoJuego} ({duracionMinutos} min)</p>
              </Col>
            </Row>
            <Alert variant="info" className="mt-2 mb-0 py-2">
              <small>
                Sólo puedes editar los jugadores. Para cambiar horario o cancha,
                {checkIfAdmin() || checkIfGestorReservas()
                  ? " elimina esta reserva y crea una nueva."
                  : " solicita a un administrador que elimine esta reserva y crea una nueva."}
              </small>
            </Alert>
          </div>

          {/* Reservante */}
          {(checkIfAdmin() || checkIfGestorReservas()) && (
            <div className="mb-4 p-3 bg-light rounded">
              <p className="mb-2">
                <strong>Reservado por:</strong>{" "}
                {reservanteCambiado
                  ? (usuario.fullName || usuario.userName)
                  : (reserva.userFullName || reserva.userName)}
                {reservanteCambiado && (
                  <span className="text-success ms-2" style={{ fontSize: "0.85em" }}>(modificado)</span>
                )}
              </p>
              {!reservanteCambiado && reserva.userName !== usuario.userName && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setReservanteCambiado(true)}
                >
                  Cambiar quien reserva
                </Button>
              )}
              {reservanteCambiado && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setReservanteCambiado(false)}
                >
                  Deshacer cambio
                </Button>
              )}
            </div>
          )}

          {/* Selección de jugadores */}
          <h6 className="mb-3">
            {cantidadJugadores === 2 ? <User size={18} className="me-2" /> : <Users size={18} className="me-2" />}
            Jugadores ({cantidadJugadores === 2 ? "Singles" : "Dobles"})
          </h6>

          {loadingSocios && (
            <Alert variant="info">Cargando socios...</Alert>
          )}

          <Row>
            {Array.from({ length: cantidadJugadores }).map((_, index) => (
              <Col md={6} key={index} className="mb-3">
                <Form.Group>
                  <Form.Label>Jugador {index + 1}</Form.Label>
                  <Select
                    value={sociosSeleccionados[index]}
                    onChange={(opcion) => handleSeleccionarSocio(index, opcion)}
                    options={getOpcionesDisponibles(index)}
                    formatOptionLabel={(option) => (
                      <div>
                        <div>{option.fullName || option.userName}</div>
                        {option.userName && option.fullName && (
                          <div style={{ fontSize: "0.8em", color: "#6c757d" }}>@{option.userName}</div>
                        )}
                      </div>
                    )}
                    placeholder={`Seleccionar jugador ${index + 1}...`}
                    isClearable
                    isSearchable
                    noOptionsMessage={() => "No hay socios disponibles"}
                    isDisabled={loadingSocios}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={loading || loadingSocios}>
            {loading ? "Guardando..." : "Guardar cambios"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ModalEditarReserva;
